import { useEffect, useState } from 'react';
import {
  DataGridPro,
  GridDensity,
  gridFilteredRowsLookupSelector,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { useFetchYtdQuery } from '@api/Trinium/Endpoints/Settlements/slice';
import { YtdSummary } from '@api/Trinium/Endpoints/Settlements/types';
import { GenericOverlay } from '@components/DataGrid/GenericNoRowsOverlay';
import BrandLinearProgress from '@/Infrastructure/Components/Loading/BrandLinearProgress';
import { SettlementsToolbar } from '../SettlementsToolbar';
import { byCustomerColumns, byServiceColumns, currencyFormatter } from './types';
import { settlementsGridSx, settlementsRowHeight } from '../gridStyles';
import { IStatementTab } from '../types';

import styles from './YtdTab.module.css';

const EMPTY_YTD: YtdSummary = { byCustomer: [], byService: [] };

const gridSlots = {
  toolbar: SettlementsToolbar,
  loadingOverlay: BrandLinearProgress,
  noRowsOverlay: () => <GenericOverlay overlayText='No YTD Charges Found.' />
};

// DataGridPro (not Premium) has no built-in aggregation, so sum the amount of the currently
// filtered rows and refresh it whenever filtering/quick-filter changes the visible set.
function useVisibleAmountTotal(
  apiRef: ReturnType<typeof useGridApiRef>,
  rows: { id: string; amount: number }[]
): number {
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const recompute = () => {
      // The lookup holds only the rows the filters removed.
      const filteredOut = gridFilteredRowsLookupSelector(apiRef);
      setTotal(rows.reduce((sum, row) => (row.id in filteredOut ? sum : sum + row.amount), 0));
    };

    recompute();
    return api.subscribeEvent('filteredRowsSet', recompute);
  }, [apiRef, rows]);

  return total;
}

function GrandTotal({ amount, loading }: { amount: number; loading: boolean }) {
  return (
    <div className={styles['total-row']}>
      <span className={styles['total-label']}>Grand Total</span>
      <span className={styles['total-amount']}>{loading ? '—' : currencyFormatter.format(amount)}</span>
    </div>
  );
}

export default function YtdTab(props: IStatementTab) {
  const { data: ytd = EMPTY_YTD, isFetching } = useFetchYtdQuery({
    terminalNumber: props.terminalNumber,
    statementId: props.statementId
  });
  const byCustomerApiRef = useGridApiRef();
  const byServiceApiRef = useGridApiRef();
  const [density, setDensity] = useState<GridDensity>('compact');

  const { byCustomer, byService } = ytd;
  const byCustomerTotal = useVisibleAmountTotal(byCustomerApiRef, byCustomer);
  const byServiceTotal = useVisibleAmountTotal(byServiceApiRef, byService);

  return (
    <div className={styles.container}>
      <div className={styles['grid-area']}>
        <h3 className={styles['grid-title']}>YTD By Customer</h3>
        <DataGridPro
          apiRef={byCustomerApiRef}
          className={styles.grid}
          columns={byCustomerColumns}
          rows={byCustomer}
          loading={isFetching}
          density={density}
          onDensityChange={setDensity}
          rowHeight={settlementsRowHeight}
          showToolbar
          disableRowSelectionOnClick
          slots={gridSlots}
          sx={settlementsGridSx}
        />
        <GrandTotal
          amount={byCustomerTotal}
          loading={isFetching}
        />
      </div>

      <div className={styles['grid-area']}>
        <h3 className={styles['grid-title']}>YTD By Service</h3>
        <DataGridPro
          apiRef={byServiceApiRef}
          className={styles.grid}
          columns={byServiceColumns}
          rows={byService}
          loading={isFetching}
          density={density}
          onDensityChange={setDensity}
          rowHeight={settlementsRowHeight}
          showToolbar
          disableRowSelectionOnClick
          slots={gridSlots}
          sx={settlementsGridSx}
        />
        <GrandTotal
          amount={byServiceTotal}
          loading={isFetching}
        />
      </div>
    </div>
  );
}
